import React from "react";
import {FlatList, StyleSheet, Text, View} from "react-native";
import {Check} from "lucide-react-native";
import {LanguageMenuEntry, getStreamDetail, getStreamLabel} from "../../player/presentation";
import {FocusablePressable} from "../FocusablePressable";
import {playerFont} from "../../theme/tokens";
import {isTelevisionRuntime} from "../../utils/platformCapabilities";

export function PlayerLanguageMenu({ entries, selectedAudioIndex, selectedSubtitleIndex, showPreferredAudio, showPreferredSubtitles, preferredAudio, preferredSubtitles, onSelectAudio, onSelectSubtitle }: { entries: LanguageMenuEntry[]; selectedAudioIndex?: number; selectedSubtitleIndex?: number | null; showPreferredAudio: boolean; showPreferredSubtitles: boolean; preferredAudio: string; preferredSubtitles: string; onSelectAudio: (index: number) => void; onSelectSubtitle: (index: number | null) => void }) {
  const television = isTelevisionRuntime();
  return (
    <FlatList
      data={entries}
      keyExtractor={(entry, index) => entry.kind === "header" ? `h-${entry.label}` : entry.kind === "off" ? "sub-off" : `${entry.kind}-${entry.stream.Index ?? index}`}
      contentContainerStyle={styles.list}
      ListFooterComponent={(showPreferredAudio || showPreferredSubtitles) ? <View style={styles.preferred}><Text style={styles.preferenceLabel}>DEFAULTS</Text><View style={styles.preferenceRow}>{showPreferredAudio ? <Text style={styles.preferredText}>Audio · {preferredAudio || "None"}</Text> : null}{showPreferredSubtitles ? <Text style={styles.preferredText}>Subtitles · {preferredSubtitles || "None"}</Text> : null}</View></View> : null}
      renderItem={({ item: entry }) => {
        if (entry.kind === "header") return (
          <View style={styles.headerRow}>
            <Text style={styles.sectionLabel}>{entry.label}</Text>
            <View style={styles.headerLine} />
          </View>
        );
        const selected = entry.kind === "off"
          ? selectedSubtitleIndex === null
          : entry.kind === "audio"
            ? entry.stream.Index === selectedAudioIndex || (selectedAudioIndex === undefined && entry.stream.IsDefault)
            : entry.stream.Index === selectedSubtitleIndex;
        return (
          <FocusablePressable
            accessibilityLabel={entry.kind === "off" ? "Turn subtitles off" : getStreamLabel(entry.stream, `${entry.kind} track`)}
            accessibilityState={{ selected }}
            onPress={(event) => {
              event.stopPropagation();
              if (entry.kind === "off") onSelectSubtitle(null);
              else if (entry.kind === "audio") onSelectAudio(entry.stream.Index ?? entry.index);
              else onSelectSubtitle(entry.stream.Index ?? entry.index);
            }}
            style={(focused, pressed, hovered) => [
              styles.item,
              television && styles.itemTelevision,
              selected && styles.itemSelected,
              (focused || hovered) && !selected && styles.itemHovered,
              pressed && styles.pressed,
            ]}
          >
            <View style={[styles.selectionMark, selected && styles.selectionMarkSelected]}>{selected ? <Check color="#fff" size={14} strokeWidth={3} /> : null}</View>
            <View style={styles.trackCopy}>
              <View style={styles.trackTitleRow}>
                <Text numberOfLines={1} style={[styles.itemTitle, television && styles.itemTitleTelevision, selected && styles.itemTitleSelected]}>
                  {entry.kind === "off" ? "Off" : getStreamLabel(entry.stream, `Track ${entry.index + 1}`)}
                </Text>
                {entry.kind !== "off" && entry.stream.IsDefault ? <Text style={styles.defaultBadge}>DEFAULT</Text> : null}
              </View>
              {entry.kind !== "off" && getStreamDetail(entry.stream) ? (
                <Text style={[styles.trackDetail, television && styles.trackDetailTelevision, selected && styles.trackDetailSelected]}>{getStreamDetail(entry.stream)}</Text>
              ) : null}
            </View>
          </FocusablePressable>
        );
      }}
    />
  );
}

const styles = StyleSheet.create({
  list: { paddingBottom: 2 },
  headerRow: { flexDirection: "row", alignItems: "center", gap: 10, marginTop: 5, marginBottom: 3 },
  headerLine: { flex: 1, height: StyleSheet.hairlineWidth, backgroundColor: "rgba(255,255,255,0.10)" },
  sectionLabel: { paddingLeft: 8, paddingVertical: 7, color: "rgba(255,255,255,0.52)", fontFamily: playerFont, fontSize: 10, lineHeight: 13, fontWeight: "900", letterSpacing: 1, textTransform: "uppercase" },
  item: { minHeight: 48, marginVertical: 2, paddingHorizontal: 9, paddingVertical: 7, borderWidth: 1, borderColor: "transparent", borderRadius: 10, flexDirection: "row", alignItems: "center", gap: 10 },
  itemTelevision: { minHeight: 58, paddingHorizontal: 12, paddingVertical: 9 },
  itemSelected: { borderColor: "rgba(255,255,255,0.14)", backgroundColor: "rgba(255,255,255,0.09)" },
  itemHovered: { borderColor: "rgba(255,255,255,0.10)", backgroundColor: "rgba(255,255,255,0.07)" },
  selectionMark: { width: 26, height: 26, borderWidth: 1, borderColor: "rgba(255,255,255,0.14)", borderRadius: 8, alignItems: "center", justifyContent: "center", backgroundColor: "rgba(255,255,255,0.04)" },
  selectionMarkSelected: { borderColor: "rgba(229,9,20,0.70)", backgroundColor: "rgba(229,9,20,0.70)" },
  itemTitle: { color: "rgba(255,255,255,0.72)", fontFamily: playerFont, fontSize: 13, lineHeight: 18, fontWeight: "500" },
  itemTitleSelected: { color: "#fff", fontWeight: "600" },
  itemTitleTelevision: { fontSize: 16, lineHeight: 21 },
  trackCopy: { minWidth: 0, flex: 1, gap: 2 },
  trackTitleRow: { minWidth: 0, flexDirection: "row", alignItems: "center", gap: 6 },
  defaultBadge: { paddingHorizontal: 4, paddingVertical: 1, borderWidth: 1, borderColor: "rgba(255,255,255,0.14)", borderRadius: 3, color: "rgba(255,255,255,0.42)", backgroundColor: "rgba(255,255,255,0.06)", fontFamily: playerFont, fontSize: 7, lineHeight: 9, fontWeight: "800" },
  trackDetail: { color: "rgba(255,255,255,0.42)", fontFamily: playerFont, fontSize: 10, lineHeight: 13, fontWeight: "500" },
  trackDetailSelected: { color: "rgba(255,255,255,0.60)" },
  trackDetailTelevision: { fontSize: 12, lineHeight: 16 },
  preferred: { marginTop: 8, paddingHorizontal: 8, paddingTop: 9, borderTopWidth: 1, borderTopColor: "rgba(255,255,255,0.10)" },
  preferenceLabel: { color: "rgba(255,255,255,0.34)", fontFamily: playerFont, fontSize: 9, lineHeight: 12, fontWeight: "900", letterSpacing: 1 },
  preferenceRow: { marginTop: 5, flexDirection: "row", flexWrap: "wrap", gap: 6 },
  preferredText: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 7, color: "rgba(255,255,255,0.58)", backgroundColor: "rgba(255,255,255,0.055)", fontFamily: playerFont, fontSize: 10, lineHeight: 14, fontWeight: "700" },
  pressed: { opacity: 0.72 },
});
